import { MonitorSettingsBase, MonitorSettings, MonitorSettingsEntry } from './MonitorSettings';
import { StringUtils } from '../Utils/StringUtils';
import { ValueEncoder } from "../Utils/ValueEncoder";
import { MonitorType } from "../Core/MonitorType";
import { IfEntry } from "../InfoSets/IfTable";


export class MonitorSettings_SnmpIfEntry extends MonitorSettingsEntry {

	public IfIndex: number;
	public get HasIfIndex(): boolean {
		return (this.IfIndex >= 0);
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public get DisplayName(): string {
		let ifName = "";

		if (this.HasName) {
			ifName = this.Name;
		}
		else if (this.HasIfIndex) {
			ifName = this.IfIndex.toString();
		}

		return ifName;
	}

	public get LoadPercentage(): number {
		return -1;
	}

	constructor(value?: any) {
		super();

		this._Init();

		switch (typeof value) {
			case "string":
				this.Name = value;
				break;
			case "number":
				this.IfIndex = value;
				break;
		}
	}

	private _Init(): void {
		this.IfIndex = -1;
		this.Name = "";
	}

	public CopyConfig(other: MonitorSettings_SnmpIfEntry): void {
		if (!other)
			return;
		if (other == this)
			return;

		super.CopyConfig(other);

		this.IfIndex = other.IfIndex;
		this.Name = other.Name;
	}

	public Write(obj: any, context: any): void {
		obj.monitored_ifs_entry = {};
		obj = obj.monitored_ifs_entry;

		super.Write(obj, context);

		if (this.HasIfIndex)
			obj.if_index = ValueEncoder.EncodeInteger(this.IfIndex);

		if (this.HasName)
			obj.name = this.Name;
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitored_ifs_entry)
			throw new Error();
		obj = obj.monitored_ifs_entry;

		super.Read(obj, context);

		if (obj.if_index !== undefined)
			this.IfIndex = ValueEncoder.DecodeInteger(obj.if_index);

		if (obj.name !== undefined)
			this.Name = obj.name;
	}
}


export class MonitorSettings_SnmpIf extends MonitorSettings<MonitorSettings_SnmpIfEntry> {

	private static readonly SNMPIF_DEFAULT_CRITICAL_THRESHOLD = 90;
	private static readonly SNMPIF_DEFAULT_WARNING_THRESHOLD = 80;

	private static readonly SNMPIF_DEFAULT_HISTORY_COUNT = 144;

	public VerifyUp: boolean;

	constructor() {
		super();

		this.MonitorType = MonitorType.SNMP_Interface;

		this.VerifyUp = true;

		this.CriticalThreshold = MonitorSettings_SnmpIf.SNMPIF_DEFAULT_CRITICAL_THRESHOLD;
		this.WarningThreshold = MonitorSettings_SnmpIf.SNMPIF_DEFAULT_WARNING_THRESHOLD;
	}

	protected get ObjSerEntriesName(): string {
		return "monitored_ifs";
	}

	protected CreateEntry(): MonitorSettings_SnmpIfEntry {
		return new MonitorSettings_SnmpIfEntry();
	}

	public Add_IfIndex(ifIndex: number): MonitorSettings_SnmpIfEntry {
		let ifEntry: MonitorSettings_SnmpIfEntry = this.Find_IfIndex(ifIndex);
		if (!ifEntry) {
			ifEntry = new MonitorSettings_SnmpIfEntry(ifIndex);
			this.Add(ifEntry);
		}

		return ifEntry;
	}

	public Add_IfEntry(ifEntry: IfEntry): MonitorSettings_SnmpIfEntry {
		console.assert(!!ifEntry);
		if (!ifEntry)
			return null;

		let monIfEntry: MonitorSettings_SnmpIfEntry = this.Find_IfIndex(ifEntry.IfIndex);
		if (!monIfEntry) {
			monIfEntry = new MonitorSettings_SnmpIfEntry(ifEntry.IfIndex);
			monIfEntry.Name = ifEntry.ShortDisplayName;
			this.Add(monIfEntry);
		}

		return monIfEntry;
	}

	public Add_IfName(ifName: string): MonitorSettings_SnmpIfEntry {
		let ifEntry: MonitorSettings_SnmpIfEntry = this.Find_IfName(ifName);

		if (!ifEntry) {
			ifEntry = new MonitorSettings_SnmpIfEntry(ifName);
			this.Add(ifEntry);
		}

		return ifEntry;
	}

	public CopySettings(other: MonitorSettingsBase): void {
		if (!other)
			return;

		if (other == this)
			return;

		let otherSettings = other as MonitorSettings_SnmpIf;
		if (!otherSettings)
			return;

		super.CopySettings(other);

		this.VerifyUp = otherSettings.VerifyUp;
	}

	public Find_IfIndex(ifIndex: number): MonitorSettings_SnmpIfEntry {
		let ifEntry: MonitorSettings_SnmpIfEntry = null;

		for (let i = 0; i < this.Count; ++i) {

			let entry: MonitorSettings_SnmpIfEntry = this.Get(i);

			if (!entry.HasIfIndex)
				continue;

			if (entry.IfIndex != ifIndex)
				continue;

			ifEntry = entry;
			break;
		}

		return ifEntry;
	}

	public Find_IfName(ifName: string): MonitorSettings_SnmpIfEntry {
		let ifEntry: MonitorSettings_SnmpIfEntry = null;

		for (let i = 0; i < this.Count; ++i) {

			let entry: MonitorSettings_SnmpIfEntry = this.Get(i);

			if (!entry.HasName)
				continue;

			if (!StringUtils.CaseInsensitiveEquals(entry.Name, ifName))
				continue;

			ifEntry = entry;
			break;
		}

		return ifEntry;
	}

	public Find(otherIfEntry: MonitorSettings_SnmpIfEntry): MonitorSettings_SnmpIfEntry {
		if (!otherIfEntry)
			return null;

		let snmpIfEntry: MonitorSettings_SnmpIfEntry = null;

		if (otherIfEntry.HasIfIndex)
			snmpIfEntry = this.Find_IfIndex(otherIfEntry.IfIndex);
		else if (otherIfEntry.HasName)
			snmpIfEntry = this.Find_IfName(otherIfEntry.Name);

		return snmpIfEntry;
	}

	public Write(obj: any, context: any): void {
		obj.monitor_settings = {};
		obj = obj.monitor_settings;

		super.Write(obj, context);

		obj.verify_up = ValueEncoder.EncodeBoolean(this.VerifyUp);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitor_settings)
			throw new Error();
		obj = obj.monitor_settings;

		super.Read(obj, context);

		if (obj.verify_up !== undefined) {
			this.VerifyUp = ValueEncoder.DecodeBoolean(obj.verify_up);
		}
	}
}

